import React, { useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import Button, { type ButtonProps } from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { MODAL_ID } from '@atlassian/jira-atlassian-navigation/src/common/constants.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { createProjectAction_projectsDirectoryV3_CreateButton$key } from '@atlassian/jira-relay/src/__generated__/createProjectAction_projectsDirectoryV3_CreateButton.graphql';
import { useSwitchModals } from '@atlassian/jira-switch-modals-controller/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

type Props = {
	dataRef: createProjectAction_projectsDirectoryV3_CreateButton$key;
};

const CreateButtonComponent = (props: ButtonProps) => {
	const createProjectGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.createProjectGalaxia
		: messages.createProject;
	const { formatMessage } = useIntl();

	return (
		<Button appearance="primary" {...props}>
			{formatMessage(createProjectGalaxia)}
		</Button>
	);
};

const DisabledCreateButtonComponent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();

	const onShowTooltip = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'projectCreateButtonDisabledTooltip',
			}),
		);
	}, [createAnalyticsEvent]);

	return (
		<Tooltip content={formatMessage(messages.createProjectDisabledTooltip)} onShow={onShowTooltip}>
			<CreateButtonComponent isDisabled />
		</Tooltip>
	);
};

const CreateButton = ({ dataRef }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'directoryHeader',
	});

	const data = useFragment<createProjectAction_projectsDirectoryV3_CreateButton$key>(
		graphql`
			fragment createProjectAction_projectsDirectoryV3_CreateButton on JiraQuery
			@argumentDefinitions(
				isAllowingEndUsersToAccessCustomTemplates: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-custom-template-allow-end-users-to-create-custom-templates.relayprovider"
				}
			) {
				canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
					@optIn(to: "JiraAction")
				shouldDisplay: canPerform(type: VIEW_PROJECT_CREATION_ENTRY, cloudId: $cloudId)
					@include(if: $isAllowingEndUsersToAccessCustomTemplates)
					@optIn(to: "JiraAction")
			}
		`,
		dataRef,
	);

	const [, { on: openProjectCreateDrawer }] = useSwitchModals(MODAL_ID.PROJECT_CREATE);

	const handleCreate = useCallback(() => {
		openProjectCreateDrawer();
		fireUIAnalytics(analyticsEvent, 'CreateProjectButton');
	}, [analyticsEvent, openProjectCreateDrawer]);

	const shouldDisplayCreateProject = fg('allow_end_users_to_access_custom_templates')
		? !!data?.shouldDisplay
		: !!data?.canCreateProject;

	const fireProjectCreateButtonComponentEvent = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'rendered',
				actionSubject: 'projectCreateButtonComponent',
			}),
			{
				shouldDisplay: !!data?.shouldDisplay,
				canCreateProject: !!data?.canCreateProject,
				shouldDisplayCreateProject,
			},
		);
	}, [
		createAnalyticsEvent,
		data?.canCreateProject,
		data?.shouldDisplay,
		shouldDisplayCreateProject,
	]);

	useEffect(() => {
		fireProjectCreateButtonComponentEvent();
	}, [fireProjectCreateButtonComponentEvent]);

	if (
		!shouldDisplayCreateProject &&
		expVal('jira_project_create_button_with_tooltip', 'isEnabled', false)
	) {
		// Poster: https://hello.atlassian.net/wiki/spaces/Spork/pages/5678308889/ONE+PAGER+Project+create+permission+awareness
		return <DisabledCreateButtonComponent />;
	}

	if (!shouldDisplayCreateProject) {
		return null;
	}

	return (
		<CreateButtonComponent
			data-testId="global-pages.directories.projects-directory-v3.create-projects-button"
			onClick={handleCreate}
		/>
	);
};

const CreateButtonContainer = (props: Props) => (
	<JSErrorBoundary
		id="create-project-button"
		packageName="jira-project-directory-page-v3"
		fallback="flag"
	>
		<CreateButton {...props} />
	</JSErrorBoundary>
);

export { CreateButtonContainer as CreateButton };
